import { KycStatus } from '@share/enums';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAPI } from 'src/hooks';
import { useDebouncedCallback } from 'use-debounce';
import { useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { user } from '@state/user';
import Countdown, { CountdownRenderProps } from 'react-countdown';
import ErrorMsg from '@com/utils/ErrorMsg';
import LoadingContainer from '@com/utils/LoadingContainer';
import MouseOver from '@com/utils/MouseOver';

interface InfoFormInputs {
  idKYC?: string;
  email: string;
  bankKYCId?: string;
  bankKYCAccountName?: string;
}

function AccountKYC(): JSX.Element {
  const { t } = useTranslation();
  const { userData } = user.useContainer();
  const [isSending, setIsSending] = useState(false);
  const { getKycStatus, sendVerifyAccountEmail, cancelBankKyc, cancelIdKyc } =
    useAPI();
  const { data, isSuccess, isError, refetch } = useQuery(
    [getKycStatus.name],
    () => getKycStatus()
  );

  const {
    register,
    formState: { errors: infoErrors },
  } = useForm<InfoFormInputs>();

  const cancelBankKYC = async () => {
    await cancelBankKyc();
    toast.success(t('toastMessage.profileUpdated'));
    await refetch();
  };

  const cancelIdKYC = async () => {
    await cancelIdKyc();
    toast.success(t('toastMessage.profileUpdated'));
    await refetch();
  };

  const verifyEmail = useDebouncedCallback(
    async () => {
      const email = userData?.user?.email;
      if (email) {
        setIsSending(true);
        const hasSent = await sendVerifyAccountEmail(email);
        if (hasSent) {
          toast.success(t('toastMessage.verifyEmailSent', { email }));
        } else {
          toast.error(t('toastMessage.verifyEmailAlreadySent'));
        }
        setTimeout(() => {
          setIsSending(false);
        }, 60000);
      }
    },

    500,
    { leading: true, trailing: false }
  );

  return (
    <LoadingContainer
      className="w-100"
      iconClassName="w-100 d-flex justify-content-center align-items-center"
      isError={isError}
      isSuccess={isSuccess}
    >
      <div className="tf-create-item tf-section" style={{ minHeight: '60vh' }}>
        <div className="themesflat-container">
          <div className="d-flex row justify-content-center">
            {/* email */}
            {!data?.email?.email.endsWith('@temp.nft-investment.io') && (
              <div className="col-xl-3 col-lg-4 col-md-6 col-12 mb-5">
                <div className="sc-card-profile text-center h-100">
                  <fieldset>
                    <h4 className="my-3" data-test="email-title">
                      {data?.email?.status && (
                        <i className="fas fa-badge-check mr-2 text-success" />
                      )}
                      {t('editProfilePage.email')}
                    </h4>
                    <input
                      aria-required
                      disabled
                      aria-label="email"
                      placeholder={t('editProfilePage.enterEmail')}
                      type="email"
                      value={
                        data?.email?.email.endsWith('@temp.nft-investment.io')
                          ? ''
                          : data?.email?.email
                      }
                      {...register('email', {
                        required: {
                          value: true,
                          message: t('errorMsg.editProfile.emailRequired'),
                        },
                        pattern: {
                          value:
                            /^\s*[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\s*$/i,
                          message: t('errorMsg.editProfile.emailPattern'),
                        },
                      })}
                    />
                    <ErrorMsg errors={infoErrors} name="email" />
                    {!data?.email?.status && (
                      <div className="my-3">
                        <button
                          className="tf-button-submit text-truncate"
                          disabled={isSending}
                          type="submit"
                          onClick={() => verifyEmail()}
                        >
                          {isSending ? (
                            <Countdown
                              date={Date.now() + 59 * 1000}
                              renderer={(props: CountdownRenderProps) => {
                                return (
                                  <span>{`${props.minutes}:${props.seconds}`}</span>
                                );
                              }}
                            />
                          ) : (
                            t('editProfilePage.verifyEmail')
                          )}
                        </button>
                      </div>
                    )}
                  </fieldset>
                </div>
              </div>
            )}
            {/* bankIdKYC */}
            <div className="col-xl-3 col-lg-4 col-md-6 col-12 mb-5">
              <div className="sc-card-profile text-center h-100">
                <fieldset>
                  <h4
                    className="my-3 d-flex justify-content-center"
                    data-test="bank-title"
                  >
                    {data?.bank?.status === KycStatus.APPROVE && (
                      <i className="fas fa-badge-check mr-2  text-success" />
                    )}
                    {data?.bank?.status === KycStatus.PENDING && (
                      <MouseOver text={t('tips.kycPending')}>
                        <i className="fas fa-alarm-exclamation mr-2 text-warning" />
                      </MouseOver>
                    )}
                    {t('editProfilePage.bankKYC')}
                  </h4>
                  <input
                    aria-required
                    disabled
                    aria-label="bankKYCId"
                    placeholder={t('editProfilePage.noBankKYC')}
                    type="text"
                    value={data?.bank?.name || ''}
                    {...register('bankKYCId')}
                  />
                  {data?.bank.status === KycStatus.PENDING ? (
                    <div className="my-3">
                      <button
                        className="btn-upload style2"
                        type="button"
                        onClick={() => cancelBankKYC()}
                      >
                        {t('editProfilePage.delete')}
                      </button>
                    </div>
                  ) : (
                    data?.bank.status !== KycStatus.APPROVE && (
                      <div className="my-3">
                        <Link
                          className="btn-upload style2"
                          to="/edit-profile/bank-KYC"
                        >
                          {t('editProfilePage.verifyNow')}
                        </Link>
                      </div>
                    )
                  )}
                </fieldset>
              </div>
            </div>
            {/* identity  */}
            <div className="col-xl-3 col-lg-4 col-md-12 col-12 mb-5">
              <div className="sc-card-profile text-center h-100">
                <fieldset>
                  <h4 className="my-3" data-test="id-title">
                    {data?.id?.status === KycStatus.APPROVE && (
                      <i className="fas fa-badge-check mr-2 text-success" />
                    )}
                    {data?.id?.status === KycStatus.PENDING && (
                      <i className="fas fa-clipboard-list mr-2 text-warning" />
                    )}

                    {t('editProfilePage.identityKYC')}
                  </h4>
                  <input
                    aria-required
                    disabled
                    aria-label="idKYC"
                    placeholder={t('editProfilePage.noIdKYC')}
                    type="text"
                    value={data?.id?.full_name || ''}
                    {...register('idKYC')}
                  />
                  {data?.id.status === KycStatus.PENDING ? (
                    <div className="my-3">
                      <button
                        className="btn-upload style2"
                        type="button"
                        onClick={() => cancelIdKYC()}
                      >
                        {t('editProfilePage.delete')}
                      </button>
                    </div>
                  ) : (
                    data?.id.status !== KycStatus.APPROVE && (
                      <div className="my-3">
                        <Link
                          className="btn-upload style2"
                          to="/edit-profile/id-KYC"
                        >
                          {t('editProfilePage.verifyNow')}
                        </Link>
                      </div>
                    )
                  )}
                </fieldset>
              </div>
            </div>
          </div>
        </div>
      </div>
    </LoadingContainer>
  );
}

export default AccountKYC;
